import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Earth, TrendingUp, Wallet, Building2, AlertTriangle, Bell, RefreshCw, FileSpreadsheet, FileText, ChevronRight, Eye, Zap } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useHoldingKpis, useRecomputeHoldingKpis } from '@/hooks/useHolding';
import type { HoldingFilialeKpi } from '@/api/holding';
import { useAuthStore } from '@/stores/authStore';
import { formatMontant, formatNumber } from '@/utils/currency';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';

/**
 * Dashboard Holding consolidé pixel-perfect CTO-FRONT-002.
 * Référence : design-dashboard-holding-v1.html
 * Données 100% live depuis GET /v1/holding/kpis (admin_holding/auditeur uniquement).
 */

const PAYS_FLAG: Record<string, string> = {
  "Côte d'Ivoire": '🇨🇮',
  'Burkina Faso':  '🇧🇫',
  'Guinée':        '🇬🇳',
};

export const HoldingPage = () => {
  const { data, isLoading, refetch, dataUpdatedAt } = useHoldingKpis();
  const recompute = useRecomputeHoldingKpis();
  const navigate = useNavigate();
  const enterPDGView = useAuthStore((s) => s.enterPDGView);

  // Indicateur temps écoulé depuis la dernière synchro (rafraîchi chaque sec)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const syncSecondsAgo = dataUpdatedAt ? Math.floor((now - dataUpdatedAt) / 1000) : 0;
  const syncLabel = !dataUpdatedAt
    ? '—'
    : syncSecondsAgo < 5   ? 'à l\'instant'
    : syncSecondsAgo < 60  ? `il y a ${syncSecondsAgo}s`
    : syncSecondsAgo < 3600 ? `il y a ${Math.floor(syncSecondsAgo / 60)}min`
    : `il y a ${Math.floor(syncSecondsAgo / 3600)}h`;

  const handleRecompute = async () => {
    try {
      await recompute.mutateAsync(undefined);
      // Force un refetch immédiat après le recalcul serveur (sinon on attend le prochain poll)
      await refetch();
    } catch (e) {
      // Erreur affichée via toast si un jour on ajoute un système de notif ;
      // pour l'instant on log silencieusement.
      // eslint-disable-next-line no-console
      console.error('[HoldingPage] recompute failed', e);
    }
  };

  // CTO-ACCESS-001 — Bouton "Voir filiale" → mode lecture seule
  const handleViewFiliale = async (f: HoldingFilialeKpi) => {
    await enterPDGView(f.filiale.filialeKey, f.filiale.name);
    navigate('/');
  };

  const filiales = data?.filiales ?? [];

  // ── A-3 : le total groupe vient du serveur ────────────────────────────
  // Cet écran additionnait lui-même les filiales. Le serveur calculait déjà
  // le même total et l'envoyait dans `groupe` — il était purement ignoré.
  // Deux calculs pour un seul chiffre : exactement la maladie qu'on soigne.
  // Le DSO moyen reste dérivé ici : c'est une moyenne d'indicateurs déjà
  // calculés par filiale, pas une addition de montants.
  const totals = useMemo(() => {
    const g = data?.groupe;
    const dsoRenseignes = filiales.filter((f) => f.dsoDays > 0);
    const ca     = BigInt(g?.caFcfaCents     ?? '0');
    const ebitda = BigInt(g?.ebitdaFcfaCents ?? '0');
    return {
      caFcfa:       Number(ca)                                  / 100,
      chargesFcfa:  Number(BigInt(g?.chargesFcfaCents    ?? '0')) / 100,
      ebitdaFcfa:   Number(ebitda)                              / 100,
      tresoFcfa:    Number(BigInt(g?.tresorerieFcfaCents ?? '0')) / 100,
      employees:    g?.employeesCount ?? 0,
      dsoAvg:       dsoRenseignes.length > 0
        ? Math.round(dsoRenseignes.reduce((s, f) => s + f.dsoDays, 0) / dsoRenseignes.length)
        : 0,
      tauxEbitda:   ca > 0n ? Number(ebitda) / Number(ca) : 0,
      sansDonnees:  g?.nombreFilialesSansDonnees ?? 0,
      filialesTotal: g?.nombreFilialesTotal ?? filiales.length,
    };
  }, [data, filiales]);

  const paysTotals = useMemo(() => {
    const map = new Map<string, { count: number; ca: number; flag: string }>();
    for (const f of filiales) {
      const pays = f.filiale.pays;
      const cur = map.get(pays) ?? { count: 0, ca: 0, flag: PAYS_FLAG[pays] ?? '🌍' };
      cur.count++;
      cur.ca += Number(BigInt(f.caFcfaCents)) / 100;
      map.set(pays, cur);
    }
    return Array.from(map.entries()).map(([pays, v]) => ({ pays, ...v }));
  }, [filiales]);

  // Alertes générées dynamiquement
  const alertes: Array<{ kind: 'aw' | 'ad' | 'ai' | 'as'; icon: string; title: string; desc: string; fil?: string }> = [];

  // A-3 : une consolidation incomplète doit le DIRE. Un total groupe auquel
  // manquent des filiales n'est pas opposable à un partenaire financier.
  if (totals.sansDonnees > 0) {
    alertes.push({
      kind:  'ad',
      icon:  '🏢',
      title: `Consolidation incomplète — ${totals.sansDonnees} filiale${totals.sansDonnees > 1 ? 's' : ''} sur ${totals.filialesTotal} sans chiffres`,
      desc:  filiales
        .filter((f) => f.motif)
        .map((f) => `${f.filiale.name} : ${f.motif === 'FILIALE_NON_RACCORDEE' ? 'base non raccordée' : 'aucun calcul sur la période'}`)
        .join(' · '),
    });
  }

  for (const f of filiales) {
    if (f.dsoDays > 120) alertes.push({
      kind: 'ad', icon: '⚠️',
      title: `DSO critique ${f.dsoDays}j`,
      desc:  'Délai de paiement clients très élevé — action urgente',
      fil:   f.filiale.name,
    });
    else if (f.dsoDays > 90) alertes.push({
      kind: 'aw', icon: '⏱',
      title: `DSO élevé ${f.dsoDays}j`,
      desc:  'Au-dessus du seuil OHADA recommandé (90j)',
      fil:   f.filiale.name,
    });
    const bfr = Number(BigInt(f.bfrCents));
    const tre = Number(BigInt(f.tresorerieCents));
    if (bfr > 0 && bfr > tre * 1.5) alertes.push({
      kind: 'aw', icon: '💰',
      title: 'BFR > Trésorerie',
      desc:  'Surveillance besoin en fonds de roulement',
      fil:   f.filiale.name,
    });
  }
  if (totals.ebitdaFcfa > 0 && totals.tauxEbitda > 0.20) alertes.push({
    kind: 'as', icon: '🏆',
    title: 'EBITDA groupe > 20%',
    desc:  `Taux ${(totals.tauxEbitda * 100).toFixed(1)}% — excellent niveau de rentabilité`,
  });
  if (alertes.length === 0) {
    alertes.push({ kind: 'ai', icon: 'ℹ️', title: 'Aucune alerte active', desc: 'Tous les indicateurs dans la norme — saisir des écritures pour activer les KPIs' });
  }

  // Objectifs CA 2026 (cibles arbitraires alignées sur la roadmap S12)
  const obj1Cible = 500_000_000;  // CA groupe annuel 500M FCFA
  const obj2Cible = 50;           // Effectif groupe 50
  const obj3Cible = 8;            // 8 filiales opérationnelles

  // ── Export consolidé Excel / PDF ───────────────────────────
  const exportColumns: ExportColumn<HoldingFilialeKpi>[] = [
    { header: 'Filiale',          accessor: (f) => f.filiale.name },
    { header: 'Pays',             accessor: (f) => f.filiale.pays },
    { header: 'Devise',           accessor: (f) => f.filiale.devise },
    { header: 'CA (FCFA)',        accessor: (f) => Number(BigInt(f.caFcfaCents))         / 100, align: 'right' },
    { header: 'Charges (FCFA)',   accessor: (f) => Number(BigInt(f.chargesFcfaCents))    / 100, align: 'right' },
    { header: 'EBITDA (FCFA)',    accessor: (f) => Number(BigInt(f.ebitdaFcfaCents))     / 100, align: 'right' },
    { header: 'Trésorerie (FCFA)', accessor: (f) => Number(BigInt(f.tresorerieFcfaCents)) / 100, align: 'right' },
    { header: 'Effectif',         accessor: (f) => f.employeesCount,                            align: 'right' },
    { header: 'DSO (jours)',      accessor: (f) => f.dsoDays,                                    align: 'right' },
  ];

  const onExportXLSX = () => exportToXLSX({
    filename:  buildExportFilename('Holding_Consolide', 'simgroup', 'xlsx'),
    sheetName: `Consolidé ${data?.periode ?? ''}`,
    columns:   exportColumns,
    rows:      filiales,
  });

  const onExportPDF = () => exportToPDF({
    title:    'SIM GROUP — Vue consolidée groupe',
    subtitle: `${filiales.length} filiales · Période ${data?.periode ?? '—'} · CA groupe ${formatNumber(totals.caFcfa)} FCFA · EBITDA ${formatNumber(totals.ebitdaFcfa)} FCFA (${(totals.tauxEbitda * 100).toFixed(1)}%)`,
    filename: buildExportFilename('Holding_Consolide', 'simgroup', 'pdf'),
    orientation: 'landscape',
    headerColor: [233, 142, 44], // Orange SIM GROUP
    columns:  exportColumns,
    rows:     filiales,
  });

  return (
    <>
      <Topbar
        title="Vue consolidée groupe"
        subtitle={`SIM GROUP Holding · ${filiales.length} filiale${filiales.length > 1 ? 's' : ''} · Période ${data?.periode ?? '—'}`}
        actions={
          <>
            <span
              className="ds-live-indicator"
              title={`Auto-refresh 30s · Dernière synchro ${syncLabel}`}
            >
              <span className="ds-live-dot" /> LIVE · {syncLabel}
            </span>
            <button
              className="ds-btn ds-btn-p ds-btn-sm"
              onClick={() => void handleRecompute()}
              disabled={recompute.isPending}
              title={`Force le recalcul complet des KPI sur les ${filiales.length} filiales actives (peut prendre ~5s)`}
            >
              <Zap size={14} className={recompute.isPending ? 'animate-spin' : ''} />
              {recompute.isPending ? 'Calcul…' : 'Recalculer'}
            </button>
            <button
              className="ds-btn ds-btn-g ds-btn-sm"
              onClick={() => void refetch()}
              aria-label="Rafraîchir depuis snapshots"
              title="Rafraîchit depuis les derniers snapshots stockés (rapide, sans recalcul)"
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportXLSX()} disabled={filiales.length === 0} title="Export Excel">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportPDF()} disabled={filiales.length === 0} title="Export PDF">
              <FileText size={14} /> PDF
            </button>
            <button className="ds-btn ds-btn-g" style={{ width: 36, height: 36, padding: 0, justifyContent: 'center' }}>
              <Bell size={16} />
            </button>
          </>
        }
      />

      <div className="ds-content">
        {/* Hero KPIs consolidés */}
        <div className="ds-hero-grid">
          <div className="ds-hk h1">
            <div className="ds-hk-ico" style={{ background: 'var(--p-soft)' }}><TrendingUp size={20} /></div>
            <div className="ds-hk-label">CA consolidé groupe</div>
            <div className="ds-hk-val">{formatNumber(totals.caFcfa)} <span className="ds-hk-unit">FCFA</span></div>
            <div className="ds-hk-trend ds-trend-up">{filiales.length} filiale{filiales.length > 1 ? 's' : ''}</div>
          </div>
          <div className="ds-hk h2">
            <div className="ds-hk-ico" style={{ background: '#d1fae5' }}><Building2 size={20} /></div>
            <div className="ds-hk-label">EBITDA groupe</div>
            <div className="ds-hk-val">{formatNumber(totals.ebitdaFcfa)} <span className="ds-hk-unit">FCFA</span></div>
            <div className="ds-hk-trend ds-trend-up">Taux {(totals.tauxEbitda * 100).toFixed(1)}%</div>
          </div>
          <div className="ds-hk h3">
            <div className="ds-hk-ico" style={{ background: '#dbeafe' }}><Wallet size={20} /></div>
            <div className="ds-hk-label">Trésorerie consolidée</div>
            <div className="ds-hk-val">{formatNumber(totals.tresoFcfa)} <span className="ds-hk-unit">FCFA</span></div>
            <div className="ds-hk-trend ds-trend-flat">DSO moy. {totals.dsoAvg}j</div>
          </div>
          <div className="ds-hk h4">
            <div className="ds-hk-ico" style={{ background: '#f3e8ff' }}><Earth size={20} /></div>
            <div className="ds-hk-label" title="Nombre d'employés RH (contrats actifs), distinct du nombre de comptes utilisateurs de l'application.">Effectif RH consolidé</div>
            <div className="ds-hk-val">{totals.employees}</div>
            <div className="ds-hk-trend ds-trend-flat">{paysTotals.length} pays</div>
          </div>
        </div>

        {/* Section : Filiales table */}
        <div className="ds-section-h">
          <div>
            <h2 className="ds-section-title">Performance des filiales</h2>
            <p className="ds-section-sub">Toutes filiales comptabilisent en FCFA (XOF) · Période {data?.periode ?? '—'}</p>
          </div>
        </div>

        <div className="ds-mg" style={{ marginBottom: 16 }}>
          {/* Filiales table */}
          <div className="ds-bp" style={{ gridColumn: 'span 1' }}>
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Filiales du groupe</div>
                <div className="ds-bp-s">{filiales.length} entité{filiales.length > 1 ? 's' : ''} consolidée{filiales.length > 1 ? 's' : ''}</div>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="ds-fil-tbl">
                <thead>
                  <tr>
                    <th>Filiale</th>
                    <th className="ds-am">CA</th>
                    <th className="ds-am">EBITDA</th>
                    <th className="ds-am">Trésorerie</th>
                    <th className="ds-am">Effectif</th>
                    <th>DSO</th>
                    <th style={{ width: 110 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
                  )}
                  {!isLoading && filiales.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Aucune filiale consolidée</td></tr>
                  )}
                  {filiales.map((f) => {
                    const ca = Number(BigInt(f.caFcfaCents)) / 100;
                    const ebi = Number(BigInt(f.ebitdaFcfaCents)) / 100;
                    const tre = Number(BigInt(f.tresorerieFcfaCents)) / 100;
                    return (
                      <tr key={f.id}>
                        <td>
                          <div className="ds-fil-name-wrap">
                            <div className="ds-fil-dot" style={{ background: f.filiale.colorPrimary }} />
                            <div>
                              <div className="ds-fil-nm">{f.filiale.name}</div>
                              <div className="ds-fil-pays">{PAYS_FLAG[f.filiale.pays] ?? '🌍'} {f.filiale.pays} · {f.filiale.devise}</div>
                            </div>
                          </div>
                        </td>
                        <td className="ds-am">{formatNumber(ca)}</td>
                        <td className="ds-am" style={ebi > 0 ? { color: 'var(--success)' } : undefined}>{formatNumber(ebi)}</td>
                        <td className="ds-am">{formatNumber(tre)}</td>
                        <td className="ds-am">{f.employeesCount}</td>
                        <td>
                          {f.dsoDays === 0
                            ? <span style={{ color: 'var(--tx-3)' }}>—</span>
                            : <span className={`ds-bd ${f.dsoDays > 120 ? 'ds-bd-d' : f.dsoDays > 90 ? 'ds-bd-w' : 'ds-bd-s'}`}>{f.dsoDays}j</span>}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ds-btn ds-btn-g ds-btn-sm"
                            onClick={() => handleViewFiliale(f)}
                            title={`Inspecter ${f.filiale.name} en lecture seule`}
                            style={{ color: 'var(--info)' }}
                          >
                            <Eye size={12} /> Voir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Objectifs strategiques */}
          <div className="ds-objectif-box">
            <div className="ds-obj-title">🎯 Objectifs stratégiques 2026</div>
            <div className="ds-obj-item">
              <div className="ds-obj-head">
                <span className="ds-obj-lbl">CA groupe annuel</span>
                <span className="ds-obj-val">{Math.min(100, Math.round((totals.caFcfa / obj1Cible) * 100))}%</span>
              </div>
              <div className="ds-obj-bar"><div className="ds-obj-fill" style={{ width: `${Math.min(100, (totals.caFcfa / obj1Cible) * 100)}%` }} /></div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
                {formatNumber(totals.caFcfa)} / {formatNumber(obj1Cible)} FCFA
              </div>
            </div>
            <div className="ds-obj-item">
              <div className="ds-obj-head">
                <span className="ds-obj-lbl">Effectif consolidé</span>
                <span className="ds-obj-val">{Math.min(100, Math.round((totals.employees / obj2Cible) * 100))}%</span>
              </div>
              <div className="ds-obj-bar"><div className="ds-obj-fill" style={{ width: `${Math.min(100, (totals.employees / obj2Cible) * 100)}%` }} /></div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
                {totals.employees} / {obj2Cible} personnes
              </div>
            </div>
            <div className="ds-obj-item">
              <div className="ds-obj-head">
                <span className="ds-obj-lbl">Filiales opérationnelles</span>
                <span className="ds-obj-val">{Math.min(100, Math.round((filiales.length / obj3Cible) * 100))}%</span>
              </div>
              <div className="ds-obj-bar"><div className="ds-obj-fill" style={{ width: `${Math.min(100, (filiales.length / obj3Cible) * 100)}%` }} /></div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 4 }}>
                {filiales.length} / {obj3Cible} filiales (cible Phase 2)
              </div>
            </div>
          </div>
        </div>

        {/* Pays + Trésorerie + Alertes */}
        <div className="ds-mg3">
          <div className="ds-bp">
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Trésorerie par filiale</div>
                <div className="ds-bp-s">Cash pooling potentiel</div>
              </div>
            </div>
            <div className="ds-bp-b">
              {filiales.map((f) => (
                <div key={f.id} className="ds-treso-item">
                  <div className="ds-treso-dot" style={{ background: f.filiale.colorPrimary }} />
                  <span className="ds-treso-name">{f.filiale.name}</span>
                  <span className="ds-treso-val">
                    {formatMontant(f.tresorerieCents)} <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>{f.filiale.devise}</span>
                  </span>
                </div>
              ))}
              {filiales.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--tx-3)', textAlign: 'center', padding: 16 }}>Aucune filiale</p>
              )}
            </div>
          </div>

          <div className="ds-bp">
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Répartition par pays</div>
                <div className="ds-bp-s">{paysTotals.length} pays</div>
              </div>
            </div>
            <div className="ds-bp-b">
              <div className="ds-pays-grid">
                {paysTotals.map((p) => (
                  <div key={p.pays} className="ds-pays-card">
                    <div className="ds-pays-flag">{p.flag}</div>
                    <div className="ds-pays-name">{p.pays}</div>
                    <div className="ds-pays-val">{formatNumber(p.ca)}</div>
                    <div className="ds-pays-sub">{p.count} filiale{p.count > 1 ? 's' : ''} · FCFA</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="ds-bp">
            <div className="ds-bp-h">
              <div>
                <div className="ds-bp-t">Alertes consolidées</div>
                <div className="ds-bp-s">{alertes.filter((a) => a.kind !== 'as' && a.kind !== 'ai').length} actives</div>
              </div>
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
            </div>
            <div className="ds-bp-b">
              {alertes.map((a, i) => (
                <div key={i} className={`ds-h-alert ${a.kind}`}>
                  <span className="ds-h-alert-ico">{a.icon}</span>
                  <div className="ds-h-alert-body">
                    <div className="ds-h-alert-title">{a.title}</div>
                    <div className="ds-h-alert-desc">{a.desc}</div>
                    {a.fil && <span className="ds-h-alert-fil">{a.fil}</span>}
                  </div>
                  <ChevronRight size={14} style={{ color: 'var(--tx-3)' }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 14, padding: 12, borderRadius: 'var(--r-sm)',
          border: '1px dashed var(--bdr)', background: 'var(--surface)',
          textAlign: 'center', fontSize: 11, color: 'var(--tx-3)',
        }}>
          Vue consolidée · Toutes filiales en FCFA (XOF), aucune conversion nécessaire · Auto-refresh 30s + recalcul serveur toutes les 15 min · Mise à jour immédiate sur validate/pay dans une filiale
          {data?.filiales[0]?.computedAt && ` · Dernier snapshot ${new Date(data.filiales[0].computedAt).toLocaleString('fr-FR')}`}
        </div>
      </div>
    </>
  );
};
